import { useCallback, useEffect, useRef, useState } from 'react';

import { Transition } from '@headlessui/react';

import { useAikoSupport } from '@/components/composite/SparksAgent/hooks/useAikoSupport';

import { classNames } from '@/utils/classNames';

import {
    SparksConversation,
    SparksFAB,
    SparksFooter,
    SparksHeader,
    SparksInput,
    type SparksMessage,
    SparksWelcome
} from '@/components/composite/SparksAgent';

const DEFAULT_HEIGHT = 500;
const MIN_HEIGHT = DEFAULT_HEIGHT;
const CONTAINER_WIDTH = 450;
/** Reserved space from viewport bottom so max height stops at the app corner (bottom-4 + extra for corner alignment). */
const BOTTOM_GAP = 32;
/** Top navbar height (px). Container max height is limited so it does not overlap the navbar. */
const TOP_NAVBAR_HEIGHT = 56;
const GAP_BELOW_NAVBAR = 4;
const STORAGE_KEY = 'sparks-container-height';
const STORAGE_KEY_OPEN = 'sparks-container-open';

export function SparksAgent() {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [messages, setMessages] = useState<SparksMessage[]>([]);
    const [containerHeight, setContainerHeight] = useState(DEFAULT_HEIGHT);
    const [isResizing, setIsResizing] = useState(false);

    const inputRef = useRef<HTMLTextAreaElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const mouseMoveRef = useRef<((e: MouseEvent) => void) | null>(null);
    const mouseUpRef = useRef<(() => void) | null>(null);

    const handleMinimize = useCallback(() => {
        setIsOpen(false);
        try {
            localStorage.setItem(STORAGE_KEY_OPEN, 'false');
        } catch {
            /* noop */
        }
    }, []);

    const { onNeedSupportClick, isAikoOpen } = useAikoSupport({ onBeforeOpen: handleMinimize });

    const isConversationActive = messages.length > 0;
    // Limit: container top stays below navbar; container bottom stays above viewport bottom gap
    const getMaxContainerHeight = useCallback(() => {
        return window.innerHeight - (TOP_NAVBAR_HEIGHT + GAP_BELOW_NAVBAR + BOTTOM_GAP);
    }, []);

    const handleOpen = useCallback(() => {
        setIsOpen(true);
        try {
            localStorage.setItem(STORAGE_KEY_OPEN, 'true');
        } catch {
            /* noop */
        }
    }, []);

    const handleNewChat = useCallback(() => {
        setMessages([]);
        setInputValue('');
    }, []);

    const handleActionClick = useCallback((text: string) => {
        setInputValue(text);
        requestAnimationFrame(() => inputRef.current?.focus());
    }, []);

    const handleSend = useCallback(() => {
        const trimmed = inputValue.trim();
        if (!trimmed) return;

        const userMessage: SparksMessage = {
            id: `user-${Date.now()}`,
            role: 'user',
            content: trimmed
        };

        const agentMessage: SparksMessage = {
            id: `agent-${Date.now()}`,
            role: 'agent',
            content:
                "Thanks for your message! I'm Sparks, your personal analyst. This is a mock response — real AI insights are coming soon."
        };

        setMessages((prev) => [...prev, userMessage, agentMessage]);
        setInputValue('');
    }, [inputValue]);

    // Resize logic: drag top edge to adjust height (desktop only)
    const handleResizeMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            setIsResizing(true);
            const startY = e.clientY;
            const startHeight = containerHeight;

            const handleMouseMove = (moveEvent: MouseEvent) => {
                const delta = startY - moveEvent.clientY;

                const newHeight = Math.min(
                    Math.max(startHeight + delta, MIN_HEIGHT),
                    getMaxContainerHeight()
                );
                setContainerHeight(newHeight);
            };

            const handleMouseUp = () => {
                setIsResizing(false);
                setContainerHeight((h) => {
                    try {
                        localStorage.setItem(STORAGE_KEY, String(h));
                    } catch {
                        /* noop */
                    }
                    return h;
                });
                mouseMoveRef.current = null;
                mouseUpRef.current = null;
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };

            mouseMoveRef.current = handleMouseMove;
            mouseUpRef.current = handleMouseUp;
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        },
        [containerHeight]
    );

    useEffect(() => {
        try {
            const storedOpen = localStorage.getItem(STORAGE_KEY_OPEN);
            if (storedOpen !== null) setIsOpen(storedOpen === 'true');
            const storedHeight = localStorage.getItem(STORAGE_KEY);
            if (storedHeight) {
                const parsed = parseFloat(storedHeight);

                if (!isNaN(parsed) && parsed >= MIN_HEIGHT)
                    setContainerHeight(Math.min(parsed, getMaxContainerHeight()));
            }
        } catch {
            /* localStorage unavailable */
        }
    }, []);

    useEffect(() => {
        return () => {
            if (mouseMoveRef.current) {
                document.removeEventListener('mousemove', mouseMoveRef.current);
            }
            if (mouseUpRef.current) {
                document.removeEventListener('mouseup', mouseUpRef.current);
            }
        };
    }, []);

    const renderContainerContent = () => (
        <>
            <SparksHeader onMinimize={handleMinimize} onNewChat={handleNewChat} />
            {isConversationActive ? (
                <SparksConversation messages={messages} />
            ) : (
                <SparksWelcome onActionClick={handleActionClick} />
            )}
            <SparksInput
                ref={inputRef}
                value={inputValue}
                onChange={setInputValue}
                onSend={handleSend}
            />
            <SparksFooter onNeedSupportClick={onNeedSupportClick} />
        </>
    );

    return (
        <>
            {/* FAB toggle - visible when Agent is closed and Aiko is closed */}
            <div
                className={classNames('fixed bottom-4 right-4 z-50', {
                    'pointer-events-none opacity-0': isOpen || isAikoOpen
                })}>
                <SparksFAB onClick={handleOpen} />
            </div>

            {/* Desktop floating container */}
            <Transition
                show={isOpen}
                enter="transition-all duration-300 ease-out"
                enterFrom="opacity-0 translate-y-4 scale-95"
                enterTo="opacity-100 translate-y-0 scale-100"
                leave="transition-all duration-200 ease-in"
                leaveFrom="opacity-100 translate-y-0 scale-100"
                leaveTo="opacity-0 translate-y-4 scale-95">
                <div
                    ref={containerRef}
                    className={classNames(
                        'fixed bottom-4 right-4 z-50',
                        'hidden flex-col overflow-hidden rounded-xl border border-icoGray-300 bg-white shadow-icoXS',
                        'md:flex'
                    )}
                    style={{
                        width: CONTAINER_WIDTH,
                        height: containerHeight
                    }}>
                    {/* Resize handle at top edge */}
                    <div
                        onMouseDown={handleResizeMouseDown}
                        className={classNames(
                            'absolute left-0 right-0 top-0 z-10 h-1.5 cursor-ns-resize',
                            'opacity-0 transition-opacity hover:opacity-100',
                            { 'opacity-100': isResizing }
                        )}
                    />
                    {renderContainerContent()}
                </div>
            </Transition>

            {/* Mobile container */}
            <Transition
                show={isOpen}
                enter="transition-all duration-300 ease-out"
                enterFrom="opacity-0 translate-y-full"
                enterTo="opacity-100 translate-y-0"
                leave="transition-all duration-200 ease-in"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-full">
                <div
                    className={classNames(
                        'fixed inset-x-3 inset-y-3 z-50 flex flex-col overflow-hidden rounded-xl bg-white shadow-icoXS',
                        'md:hidden'
                    )}>
                    {renderContainerContent()}
                </div>
            </Transition>
        </>
    );
}
